import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Download, 
  Calendar, 
  TrendingUp, 
  Activity,
  FileText,
  BarChart3,
  PieChart,
  Users,
  DollarSign,
  Clock,
  Settings
} from 'lucide-react';

interface ReportTemplate {
  id: string;
  title: string;
  description: string;
  category: 'financial' | 'operational' | 'performance' | 'executive';
  icon: React.ComponentType<{ className?: string }>;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  lastGenerated: string;
  size: string;
  status: 'ready' | 'generating' | 'scheduled';
}

interface GeneratedReport {
  id: string;
  title: string;
  type: string;
  generatedDate: string;
  size: string;
  status: 'completed' | 'failed' | 'processing';
  downloadUrl?: string;
}

export function AdminReports() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const reportTemplates: ReportTemplate[] = [
    {
      id: '1',
      title: 'Reporte Mensual Ejecutivo',
      description: 'Resumen completo de KPIs, ROI y métricas clave para la dirección',
      category: 'executive',
      icon: TrendingUp,
      frequency: 'monthly',
      lastGenerated: '2024-06-01',
      size: '2.3 MB',
      status: 'ready'
    },
    {
      id: '2',
      title: 'Análisis Financiero Detallado',
      description: 'Desglose de costos, revenue y proyecciones financieras',
      category: 'financial',
      icon: DollarSign,
      frequency: 'monthly',
      lastGenerated: '2024-06-15',
      size: '1.8 MB',
      status: 'ready'
    },
    {
      id: '3',
      title: 'Performance de Bots',
      description: 'Métricas de rendimiento, uptime y eficiencia por bot',
      category: 'performance',
      icon: Activity,
      frequency: 'weekly',
      lastGenerated: '2024-06-20',
      size: '950 KB',
      status: 'generating'
    },
    {
      id: '4',
      title: 'Dashboard Operacional',
      description: 'Estado del sistema, alertas y métricas operacionales',
      category: 'operational',
      icon: BarChart3,
      frequency: 'daily',
      lastGenerated: '2024-06-25',
      size: '650 KB',
      status: 'ready'
    },
    {
      id: '5',
      title: 'Análisis de Clientes',
      description: 'Health score, satisfacción y análisis de churn por cliente',
      category: 'performance',
      icon: Users,
      frequency: 'monthly',
      lastGenerated: '2024-06-10',
      size: '1.2 MB',
      status: 'scheduled'
    },
    {
      id: '6',
      title: 'Tendencias y Proyecciones',
      description: 'Análisis predictivo y tendencias de crecimiento',
      category: 'executive',
      icon: PieChart,
      frequency: 'quarterly',
      lastGenerated: '2024-04-01',
      size: '3.1 MB',
      status: 'ready'
    }
  ];

  const recentReports: GeneratedReport[] = [
    {
      id: '1',
      title: 'Reporte Mensual - Junio 2024',
      type: 'Ejecutivo',
      generatedDate: '2024-06-25T10:30:00',
      size: '2.3 MB',
      status: 'completed',
      downloadUrl: '#'
    },
    {
      id: '2',
      title: 'Análisis Financiero - Q2 2024',
      type: 'Financiero',
      generatedDate: '2024-06-24T15:45:00',
      size: '1.8 MB',
      status: 'completed',
      downloadUrl: '#'
    },
    {
      id: '3',
      title: 'Performance Semanal - Semana 25',
      type: 'Performance',
      generatedDate: '2024-06-23T09:15:00',
      size: '950 KB',
      status: 'processing'
    },
    {
      id: '4',
      title: 'Dashboard Operacional - 22/06',
      type: 'Operacional',
      generatedDate: '2024-06-22T23:59:00',
      size: '650 KB',
      status: 'completed',
      downloadUrl: '#'
    },
    {
      id: '5',
      title: 'Análisis de Clientes - Mayo 2024',
      type: 'Performance',
      generatedDate: '2024-06-20T14:20:00',
      size: '1.2 MB',
      status: 'failed'
    }
  ];

  const getCategoryColor = (category: string) => {
    const colors = {
      executive: 'bg-purple-100 text-purple-800',
      financial: 'bg-green-100 text-green-800',
      performance: 'bg-blue-100 text-blue-800',
      operational: 'bg-orange-100 text-orange-800'
    };
    return colors[category as keyof typeof colors] || colors.operational;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      ready: 'text-green-600 bg-green-50',
      generating: 'text-blue-600 bg-blue-50',
      scheduled: 'text-yellow-600 bg-yellow-50',
      completed: 'text-green-600 bg-green-50',
      failed: 'text-red-600 bg-red-50',
      processing: 'text-blue-600 bg-blue-50'
    };
    return colors[status as keyof typeof colors] || colors.ready;
  };

  const getFrequencyColor = (frequency: string) => {
    const colors = {
      daily: 'bg-red-100 text-red-800',
      weekly: 'bg-yellow-100 text-yellow-800',
      monthly: 'bg-blue-100 text-blue-800',
      quarterly: 'bg-purple-100 text-purple-800'
    };
    return colors[frequency as keyof typeof colors] || colors.monthly;
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? reportTemplates 
    : reportTemplates.filter(template => template.category === selectedCategory);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminPageLayout
      title="Centro de Reportes"
      subtitle="Generación automática de reportes ejecutivos y operacionales"
      actions={
        <>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Configurar
          </Button>
          <Button>
            <Download className="w-4 h-4 mr-2" />
            Exportar Todo
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Métricas de reportes */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{reportTemplates.length}</div>
              <div className="text-sm text-muted-foreground">Templates</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {recentReports.filter(r => r.status === 'completed').length}
              </div>
              <div className="text-sm text-muted-foreground">Completados</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {reportTemplates.filter(t => t.status === 'scheduled').length}
              </div>
              <div className="text-sm text-muted-foreground">Programados</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {recentReports.reduce((acc, r) => acc + parseFloat(r.size.replace(' MB', '').replace(' KB', '').replace(' GB', '')), 0).toFixed(1)}
              </div>
              <div className="text-sm text-muted-foreground">MB Generados</div>
            </CardContent>
          </Card>
        </div>

        {/* Templates de reportes */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Templates de Reportes</h2>
            <div className="flex gap-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
              >
                Todos
              </Button>
              <Button
                variant={selectedCategory === 'executive' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('executive')}
              >
                Ejecutivo
              </Button>
              <Button
                variant={selectedCategory === 'financial' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('financial')}
              >
                Financiero
              </Button>
              <Button
                variant={selectedCategory === 'performance' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('performance')}
              >
                Performance
              </Button>
              <Button
                variant={selectedCategory === 'operational' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('operational')}
              >
                Operacional
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const Icon = template.icon;
              return (
                <Card key={template.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{template.title}</CardTitle>
                          <CardDescription className="text-sm">
                            {template.description}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge className={getCategoryColor(template.category)}>
                        {template.category === 'executive' && 'Ejecutivo'}
                        {template.category === 'financial' && 'Financiero'}
                        {template.category === 'performance' && 'Performance'}
                        {template.category === 'operational' && 'Operacional'}
                      </Badge>
                      <Badge className={getFrequencyColor(template.frequency)}>
                        {template.frequency === 'daily' && 'Diario'}
                        {template.frequency === 'weekly' && 'Semanal'}
                        {template.frequency === 'monthly' && 'Mensual'}
                        {template.frequency === 'quarterly' && 'Trimestral'}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Último generado:</span>
                      <span className="font-medium">{new Date(template.lastGenerated).toLocaleDateString()}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Tamaño:</span>
                      <span className="font-medium">{template.size}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <Badge className={getStatusColor(template.status)}>
                        {template.status === 'ready' && 'Listo'}
                        {template.status === 'generating' && 'Generando'}
                        {template.status === 'scheduled' && 'Programado'}
                      </Badge>
                      <Button size="sm" disabled={template.status === 'generating'}>
                        {template.status === 'generating' ? 'Generando...' : 'Generar'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Reportes recientes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Reportes Recientes
            </CardTitle>
            <CardDescription>
              Últimos reportes generados y su estado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-3 font-medium">Reporte</th>
                    <th className="pb-3 font-medium">Tipo</th>
                    <th className="pb-3 font-medium">Generado</th>
                    <th className="pb-3 font-medium">Tamaño</th>
                    <th className="pb-3 font-medium">Estado</th>
                    <th className="pb-3 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReports.map((report) => (
                    <tr key={report.id} className="border-b hover:bg-muted/30">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{report.title}</span>
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant="outline">{report.type}</Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {formatDate(report.generatedDate)}
                        </div>
                      </td>
                      <td className="py-3 text-sm">{report.size}</td>
                      <td className="py-3">
                        <Badge className={getStatusColor(report.status)}>
                          {report.status === 'completed' && 'Completado'}
                          {report.status === 'failed' && 'Error'}
                          {report.status === 'processing' && 'Procesando'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        {report.status === 'completed' && (
                          <Button size="sm" variant="outline">
                            <Download className="h-3 w-3 mr-1" />
                            Descargar
                          </Button>
                        )}
                        {report.status === 'failed' && (
                          <Button size="sm" variant="outline">
                            Reintentar
                          </Button>
                        )}
                        {report.status === 'processing' && (
                          <span className="text-sm text-muted-foreground">Procesando...</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageLayout>
  );
}